import { useEffect, useState } from "react";
import { useStore } from "zustand";
import { useShallow } from "zustand/react/shallow";

import { transcribeVoiceRecording } from "../voiceTranscription";
import {
  createVoiceClipController,
  type VoiceClip,
  type VoiceClipStoreState,
} from "./voiceClipController";
import {
  createBrowserVoiceClipCoordination,
  createIndexedDbVoiceClipPersistence,
  createMemoryVoiceClipPersistence,
} from "./voiceClipPersistence";

export const voiceClips = createVoiceClipController({
  persistence:
    typeof indexedDB === "undefined"
      ? createMemoryVoiceClipPersistence()
      : createIndexedDbVoiceClipPersistence(),
  coordination: createBrowserVoiceClipCoordination(),
  transcriber: (request) => transcribeVoiceRecording(request),
});

export function selectVoiceClipsForTarget(
  state: VoiceClipStoreState,
  targetKey: string,
): VoiceClip[] {
  return Object.values(state.clips)
    .filter((clip) => clip.targetKey === targetKey)
    .toSorted((left, right) => left.createdAt - right.createdAt);
}

export function useVoiceClips(targetKey: string): ReadonlyArray<VoiceClip> {
  useEffect(() => {
    void voiceClips.ensureHydrated();
  }, []);
  return useStore(
    voiceClips.store,
    useShallow((state) => selectVoiceClipsForTarget(state, targetKey)),
  );
}

export function useVoiceClipAudio(clip: VoiceClip | null): Blob | null {
  const clipId = clip?.id ?? null;
  const ready = clip !== null && clip.status !== "recording";
  const [loaded, setLoaded] = useState<{ readonly clipId: string; readonly audio: Blob } | null>(
    null,
  );
  useEffect(() => {
    if (!clipId || !ready) return;
    let cancelled = false;
    void voiceClips.readAudio(clipId).then((audio) => {
      if (!cancelled && audio) setLoaded({ clipId, audio });
    });
    return () => {
      cancelled = true;
    };
  }, [clipId, ready]);
  return loaded && loaded.clipId === clipId ? loaded.audio : null;
}
